import React, { useEffect, useMemo, useState } from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS } from './tokens';
import { PortfolioTotals } from './PortfolioTotals';
import { A1FlagshipCard } from './A1FlagshipCard';
import { PortfolioSortRail, type SortKey } from './PortfolioSortRail';
import { PortfolioColumnHeader } from './PortfolioColumnHeader';
import { PortfolioRow } from './PortfolioRow';
import { PortfolioMap } from './PortfolioMap';
import { PortfolioSidePanel } from './PortfolioSidePanel';
import { fetchPortfolio, type PortfolioResponse, type PortfolioRow as PortfolioRowType } from '../../lib/portfolioApi';
import { comparePipelinePotential } from '../../lib/pipeline-reference';
import { createPortfolioShareToken, type CreatePortfolioShareTokenResponse } from '../../lib/admin-api';

export type ShareModalState =
  | { open: false }
  | { open: true; submitting?: boolean; error?: string | null; created?: CreatePortfolioShareTokenResponse };

interface Props {
  clientSlug: string;
  token?: string;
}

const GRADE_RANK: Record<string, number> = {
  'A+': 0, 'A': 1, 'A-': 2, 'B+': 3, 'B': 4, 'B-': 5, 'C+': 6, 'C': 7, 'C-': 8, 'D': 9,
};

function sortRows(rows: PortfolioRowType[], key: SortKey): PortfolioRowType[] {
  const copy = rows.slice();
  switch (key) {
    case 'rank':
      return copy.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    case 'mw':
      return copy.sort(comparePipelinePotential);
    case 'resolve':
      return copy.sort((a, b) => a.resolve_count - b.resolve_count);
    case 'acres':
      return copy.sort((a, b) => (b.key_numbers.acres ?? -1) - (a.key_numbers.acres ?? -1));
    case 'grade':
      return copy.sort((a, b) => (GRADE_RANK[a.grade ?? ''] ?? 99) - (GRADE_RANK[b.grade ?? ''] ?? 99));
  }
}

export const PortfolioPage: React.FC<Props> = ({ clientSlug, token }) => {
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  // Active = which row the user has clicked or which map dot is selected.
  // Drives map dot accent + flyTo, and the active visual state on the row.
  // Click-to-select; click elsewhere to move. Active does NOT auto-expand —
  // see expandedParcelId for the expansion state.
  const [activeParcelId, setActiveParcelId] = useState<string | null>(null);
  // Expanded = which row is showing its per-dimension detail panel. Separate
  // from active so row body / map dot clicks don't auto-expand. Toggled by
  // clicking the DimensionBar chips area of a row. When the active selection
  // changes to a different row, expansion collapses.
  const [expandedParcelId, setExpandedParcelId] = useState<string | null>(null);
  // Hover = transient sync between map dots and row list. Map dot hover sets
  // it; row hover could too (deferred). Drives mild visual emphasis on the
  // mirrored side but never auto-scrolls.
  const [hoveredParcelId, setHoveredParcelId] = useState<string | null>(null);
  // Share-link modal state. Mirrors zone briefing's pattern: closed -> open
  // (form) -> submitting -> created (URL + copy button). Hidden entirely for
  // share-link viewers (token prop set) — they can't create new links.
  const [shareModal, setShareModal] = useState<ShareModalState>({ open: false });

  // When active changes to a different row, collapse any prior expansion.
  // (Clicking the same row keeps its expansion state; clicking grades is the
  // only way to toggle expansion.)
  useEffect(() => {
    if (expandedParcelId && expandedParcelId !== activeParcelId) {
      setExpandedParcelId(null);
    }
  }, [activeParcelId, expandedParcelId]);

  useEffect(() => {
    let mounted = true;
    fetchPortfolio(clientSlug, token)
      .then(d => { if (mounted) setData(d); })
      .catch(e => { if (mounted) setError(e.message || 'Failed to load'); });
    return () => { mounted = false; };
  }, [clientSlug, token]);

  const sortedRows = useMemo(() => (data ? sortRows(data.rows, sortKey) : []), [data, sortKey]);

  // Cycle-wide score distribution — feeds the side panel's "Top X%" stamp.
  const cycleScores = useMemo(
    () => (data ? data.rows.map(r => r.score).filter((s): s is number => s != null) : []),
    [data]
  );

  // Currently-expanded row (if any) — drives the side panel content.
  const expandedRow = useMemo(
    () => (data && expandedParcelId ? data.rows.find(r => r.parcel_id === expandedParcelId) ?? null : null),
    [data, expandedParcelId]
  );

  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  // Open in a new tab so the portfolio view stays open underneath — the user
  // is screening across many sites and frequently wants to flip back without
  // losing scroll position / expanded-row state.
  const handleOpenBriefing = (parcelId: string) => {
    window.open(`/briefing/${parcelId}${tokenParam}`, '_blank', 'noopener,noreferrer');
  };
  const handleOpenMemo = (parcelId: string) => {
    window.open(`/briefing/${parcelId}/v2${tokenParam}`, '_blank', 'noopener,noreferrer');
  };

  // When the active parcel changes (via row click OR map dot click), bring the
  // matching row into view inside the rows scroll pane. block:'nearest' avoids
  // a jarring jump when the row is already visible.
  useEffect(() => {
    if (!activeParcelId) return;
    const el = document.querySelector(`[data-parcel-row-id="${activeParcelId}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeParcelId]);

  if (error) {
    return (
      <div style={{ padding: 80, textAlign: 'center', fontFamily: PORTFOLIO_TOKENS.sans, color: REPORT.body }}>
        <h1 style={{ fontFamily: PORTFOLIO_TOKENS.serif, color: REPORT.heading, fontWeight: 400 }}>Portfolio unavailable</h1>
        <p style={{ marginTop: 12, color: REPORT.secondary }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 80, textAlign: 'center', fontFamily: PORTFOLIO_TOKENS.mono, color: REPORT.tertiary, fontSize: 11, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
        Loading portfolio…
      </div>
    );
  }

  const cycleDateLabel = new Date(data.cycle.date).toISOString().slice(0, 10);

  return (
    // App-shell layout: outer container fills the viewport and never scrolls
    // itself. Only the rows section scrolls. This is the structural fix for
    // the row-ghosting-under-header problem — there is no shared scroll
    // context between the chrome and the list, so nothing can cross.
    <div style={{
      background: REPORT.white,
      height: '100vh',
      overflow: 'hidden',
      fontFamily: PORTFOLIO_TOKENS.sans,
      color: REPORT.body,
      fontSize: 12.5,
      lineHeight: 1.5,
      padding: '12px 24px 0',
      display: 'flex',
      justifyContent: 'center',
    }}>
      <main style={{
        width: 1280,
        maxWidth: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}>
        {/* Navy band masthead — visual rhythm matched to Briefing v1's cover
            header (BriefingContent.tsx:3283-3315): REPORT.heading background,
            patina eyebrow + brand line, white serif title. Compressed to a
            single row so the locked top chrome stays minimal. */}
        <header style={{
          background: REPORT.heading,
          borderRadius: 4,
          padding: '10px 18px',
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 18,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
            {/* Atwater × Integra co-brand lockup. Atwater mark is the
                mountain symbol (square-ish); Integra is a wide wordmark.
                Heights tuned so the two read as paired marks rather than
                competing logos. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <img
                src="/atwater-logo-light.svg"
                alt="Atwater Energy"
                style={{ height: 28, width: 'auto', display: 'block' }}
              />
              <span style={{
                fontFamily: PORTFOLIO_TOKENS.serif,
                fontSize: 16,
                fontStyle: 'italic',
                color: PORTFOLIO_TOKENS.patina,
                opacity: 0.75,
                lineHeight: 1,
              }}>
                ×
              </span>
              <img
                src="/integra-logo-light.webp"
                alt="Integra"
                style={{ height: 16, width: 'auto', display: 'block', opacity: 0.95 }}
              />
            </div>
            <span style={{
              fontFamily: PORTFOLIO_TOKENS.mono,
              fontSize: 9.5,
              fontWeight: 500,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: PORTFOLIO_TOKENS.patina,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              borderLeft: `1px solid rgba(212,152,128,0.28)`,
              paddingLeft: 14,
            }}>
              BTM Site Triage · Cycle {String(data.cycle.no).padStart(3, '0')} · {cycleDateLabel}
            </span>
          </div>

          {/* Share button — exact style match to the Revenant zone briefing's
              toolbar `btn` (ZoneBriefing.tsx:3280). Hidden when viewing through
              a share token (recipients shouldn't mint more links). */}
          <div>
            {!token && (
              <button type="button" style={mastheadBtn} onClick={() => setShareModal({ open: true })}>
                Share
              </button>
            )}
          </div>
        </header>

        {shareModal.open && (
          <PortfolioShareLinkModal
            clientSlug={clientSlug}
            state={shareModal}
            onChange={setShareModal}
            onClose={() => setShareModal({ open: false })}
          />
        )}

        {/* Map — locked just below the thin header. This is the only piece of
            data chrome that stays visible while the user scrolls the data
            below. Everything else (totals, sort, col header, rows) lives in
            the scroll section so the rows pane gets as much vertical room as
            possible. */}
        <div style={{ flexShrink: 0 }}>
          <PortfolioMap
            rows={data.rows}
            cycleBbox={data.synthesis?.bbox || null}
            onSelect={setActiveParcelId}
            onHover={setHoveredParcelId}
            activeParcelId={activeParcelId}
            sticky={false}
            height={300}
            shareToken={token}
          />
        </div>

        {/* Locked shelf — small fixed-height strip between the map and the
            scrolling content. Stays visible while parcel cards scroll, so the
            content "tucks under" a clean opaque edge instead of butting flush
            against the map's bottom border. */}
        <div style={{
          flexShrink: 0,
          height: 14,
          background: REPORT.white,
        }} />

        {/* Scroll section — Totals, sort rail, column header, and rows all
            scroll together inside this one container. flex:1 + minHeight:0
            lets it consume the remaining viewport height. */}
        <section
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            paddingRight: 6,
            paddingBottom: 24,
          }}
        >
          <PortfolioTotals totals={data.totals} />
          {data.flagship && <A1FlagshipCard flagship={data.flagship} token={token} />}

          <div style={{
            background: REPORT.white,
            borderBottom: `1px solid ${REPORT.rule}`,
            padding: '0 16px',
          }}>
            <PortfolioSortRail
              rowCount={sortedRows.length}
              totalCount={data.totals.sites}
              active={sortKey}
              onChange={setSortKey}
            />
            <PortfolioColumnHeader />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
            {sortedRows.map((row, i) => (
              <PortfolioRow
                key={row.site_evaluation_id}
                row={row}
                rowNumber={i + 1}
                isActive={row.parcel_id === activeParcelId}
                isHovered={row.parcel_id === hoveredParcelId}
                isExpanded={row.parcel_id === expandedParcelId}
                onSelect={setActiveParcelId}
                onHover={setHoveredParcelId}
                onToggleExpand={(pid) =>
                  setExpandedParcelId(prev => (prev === pid ? null : pid))
                }
              />
            ))}
          </div>
        </section>
      </main>

      {/* Right-docked side panel — opens when a row is expanded (grade chip
          or right-rail card clicked). Replaces the old inline expansion so
          the row list stays dense and scannable. */}
      <PortfolioSidePanel
        row={expandedRow}
        cycleScores={cycleScores}
        onClose={() => setExpandedParcelId(null)}
        onOpenBriefing={handleOpenBriefing}
        onOpenMemo={handleOpenMemo}
      />
    </div>
  );
};

// Share-link create modal. Owner-only — POSTs to /api/admin/portfolio-share-tokens
// (server-side requireOwner). Hidden for share-link viewers because they reach
// the page with a token prop set, which disables the trigger button.
export const PortfolioShareLinkModal: React.FC<{
  clientSlug: string;
  state: Extract<ShareModalState, { open: true }>;
  onChange: (s: ShareModalState) => void;
  onClose: () => void;
}> = ({ clientSlug, state, onChange, onClose }) => {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(180);
  const [copied, setCopied] = useState(false);
  const created = state.created;

  const submit = async () => {
    onChange({ open: true, submitting: true, error: null });
    try {
      const trimmed = label.trim();
      const result = await createPortfolioShareToken(clientSlug, trimmed || undefined, days);
      onChange({ open: true, created: result });
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const msg = e?.response?.data?.error || e?.message || 'Failed to create share link';
      const status = e?.response?.status;
      onChange({
        open: true,
        error: (status === 401 || status === 403)
          ? 'Owner role required to create share links.'
          : msg,
      });
    }
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // older browsers; let the user select manually
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(20,28,40,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: REPORT.white, padding: 26, borderRadius: 6,
          width: 460, maxWidth: '90vw',
          boxShadow: '0 18px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.5,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: REPORT.tertiary, marginBottom: 6, fontWeight: 700,
        }}>
          Portfolio share link
        </div>
        <h2 style={{
          fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 20,
          margin: 0, color: REPORT.heading, marginBottom: 4,
          letterSpacing: '-0.005em',
        }}>
          {clientSlug.charAt(0).toUpperCase() + clientSlug.slice(1)} · Cycle review
        </h2>
        <div style={{
          fontSize: 12, color: REPORT.secondary,
          fontFamily: PORTFOLIO_TOKENS.sans, marginBottom: 18, lineHeight: 1.4,
        }}>
          Tracked, recipient-labelled, revocable. Recipient can read this portfolio dashboard
          and drill into the briefing for any parcel in the current cycle.
        </div>

        {!created && (
          <>
            <label style={modalLabel}>
              Recipient label
              <input
                type="text"
                placeholder='e.g. "Integra — head of land"'
                value={label}
                onChange={e => setLabel(e.target.value)}
                maxLength={100}
                style={modalInput}
              />
            </label>
            <label style={modalLabel}>
              Expires in
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input
                  type="number" min={1} max={365}
                  value={days}
                  onChange={e => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v)) setDays(Math.min(365, Math.max(1, Math.floor(v))));
                  }}
                  style={{ ...modalInput, width: 80, marginTop: 0 }}
                />
                <span style={{ color: REPORT.secondary, fontSize: 12 }}>days (1-365, default 180)</span>
              </div>
            </label>
            {state.error && (
              <div style={{
                color: REPORT.fail, fontSize: 12, marginTop: 10,
                fontFamily: PORTFOLIO_TOKENS.sans,
              }}>{state.error}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
              <button style={modalBtnSecondary} onClick={onClose} disabled={state.submitting}>Cancel</button>
              <button style={modalBtnPrimary} onClick={submit} disabled={state.submitting}>
                {state.submitting ? 'Creating…' : 'Create link'}
              </button>
            </div>
          </>
        )}

        {created && (
          <>
            <div style={{
              fontSize: 12, color: REPORT.body,
              fontFamily: PORTFOLIO_TOKENS.sans, marginBottom: 8, lineHeight: 1.5,
            }}>
              {created.label
                ? <>Recipient: <b>{created.label}</b></>
                : <span style={{ color: REPORT.tertiary }}>No recipient label</span>}
              <br />
              Expires: <b>{new Date(created.expires_at).toLocaleDateString()}</b>
            </div>
            <div style={{
              background: REPORT.surface,
              border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`,
              padding: 10, borderRadius: 4, marginTop: 8,
            }}>
              <code style={{
                fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 11,
                color: REPORT.body, wordBreak: 'break-all',
              }}>
                {created.url}
              </code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={modalBtnSecondary} onClick={copy}>{copied ? 'Copied' : 'Copy URL'}</button>
              <button style={modalBtnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Toolbar button — exact match to ZoneBriefing.tsx:3280 `btn`. Same border /
// patina / sans-12 so the Portfolio masthead and Zone briefing toolbar read
// as the same product when a recipient flips between them.
export const mastheadBtn: React.CSSProperties = {
  marginLeft: 8,
  padding: '4px 10px',
  background: 'transparent',
  border: '1px solid rgba(212,152,128,0.4)',
  color: REPORT.patina,
  borderRadius: 3,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: PORTFOLIO_TOKENS.sans,
};

const modalLabel: React.CSSProperties = {
  display: 'block', fontSize: 10.5, color: REPORT.heading,
  fontFamily: PORTFOLIO_TOKENS.sans, fontWeight: 600,
  letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 14,
};
const modalInput: React.CSSProperties = {
  width: '100%', padding: '8px 10px', marginTop: 4,
  border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`, borderRadius: 3,
  fontSize: 13, fontFamily: PORTFOLIO_TOKENS.sans,
  color: REPORT.body, background: REPORT.white,
  boxSizing: 'border-box',
};
const modalBtnPrimary: React.CSSProperties = {
  padding: '8px 16px', fontSize: 11.5, fontWeight: 700,
  fontFamily: PORTFOLIO_TOKENS.mono, letterSpacing: '0.08em',
  textTransform: 'uppercase',
  background: REPORT.heading, color: REPORT.white,
  border: `1px solid ${REPORT.heading}`, borderRadius: 3, cursor: 'pointer',
};
const modalBtnSecondary: React.CSSProperties = {
  padding: '8px 16px', fontSize: 11.5,
  fontFamily: PORTFOLIO_TOKENS.mono, letterSpacing: '0.08em',
  textTransform: 'uppercase',
  background: REPORT.white, color: REPORT.body,
  border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`, borderRadius: 3, cursor: 'pointer',
};
